import {
  locationToOpenSettings,
  useIsShowingFullscreenOverlay,
} from '#app/containers/routes';
import { useTokenizedApi } from '@tokenized/sdk-react-private';
import UserCircleIcon from '#components/Elements/SVGIcon/UserCircleIcon';
import HelpCircleIcon from '#components/Elements/SVGIcon/HelpCircleIcon';
import ActivityIndicator from '#components/Pages/Overview/ActivityTable/ActivityIndicator';
import Button from '#components/Elements/Button/Button';
import { errorResponseNotify } from '#modules/utils/errors';
import Tooltip from '#components/Elements/Tooltip/Tooltip';
import EntityIcon from '#components/Elements/EntityAvatar/EntityIcon';
import {
  useSelectedProfileId,
  useSelectedEntityId,
} from '#modules/state/ProfileState';
import classNames from 'classnames';
import { default as React } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useRedirect } from '#modules/hooks/navigation';
import { EntityContext } from '#modules/context/EntityContext';
import TokenizedLogo from '#components/Elements/SVGIcon/TokenizedLogo';
import {
  useAnyActivityCount,
  useLiquidNobleActiveProfiles,
  useAnyProviderLoggedIn,
  useAnyProviderEntity,
  useLockboxBalanceRefetch,
} from '#modules/api/User';
import { saveEntityId, saveProfileId } from '#modules/utils/jwt';
import { deploymentUrl } from '#config/definitions';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';
import styles from './MainToolbar.scss';

function SupportPopover({ onClose, supportButtonRef }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        (!supportButtonRef.current ||
          !supportButtonRef.current.contains(e.target))
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose, supportButtonRef]);

  return (
    <div ref={ref} className={styles.supportPopover}>
      <p className={styles.supportPopoverText}>
        <FormattedMessage
          defaultMessage="Have a question or need a hand? We're happy to help with anything, whether it's a technical issue, a billing query, or something to do with your account."
          description="Support popover body text"
        />
      </p>
      <p className={styles.supportPopoverNote}>
        <FormattedMessage
          defaultMessage="For your security, please never share your password or any verification codes with us."
          description="Support popover security note"
        />
      </p>
      <a
        href="mailto:hello@liquidnoble.com"
        className={styles.supportPopoverEmail}
      >
        hello@liquidnoble.com
      </a>
    </div>
  );
}

function ProfileIcon({
  profile,
  selectedProfile,
  setIsMenuOpen,
  loadProfileList,
  refetchCombinedBalance,
}) {
  const intl = useIntl();
  // const history = useHistory();
  // const location = useLocation();
  const redirect = useRedirect();
  const tokenizedApi = useTokenizedApi();
  const liquidNobleApi = useLiquidNobleApi();
  const overlayVisible = useIsShowingFullscreenOverlay();
  const entityId = useSelectedEntityId();
  const { activeEntityId, setActiveEntityId, setActiveLockboxId } =
    React.useContext(EntityContext);
  // const personalProfileId = useActivePersonalProfile()?.id;
  const selected = profile.id === selectedProfile?.id;

  const entity = useAnyProviderEntity(profile.id, profile.entityId, true);
  const { isTokenizedEntity } = profile || {};
  const isSelectedTokenized = selectedProfile?.isTokenizedEntity;

  // Make sure that deploymentUrl ends with a slash
  const lqnAuthUrl = `${deploymentUrl.replace(/\/$/, '')}/log-out?handle=${entity?.handle}`;
  const openOAuth2URL = React.useMemo(
    () => async () => {
      const url = await tokenizedApi.account.getLoginURL();
      if (url) {
        // Open in a new tab
        window.open(url, '_blank')?.focus();
      }
    },
    [tokenizedApi],
  );

  React.useEffect(() => {
    if (entityId && activeEntityId !== entityId) {
      setActiveEntityId(entityId);
    }
  }, [entityId, activeEntityId, setActiveEntityId]);

  const tooltipText = React.useMemo(() => {
    if (!entity) {
      return '';
    }

    return intl.formatMessage(
      {
        defaultMessage: '{entity}{br}{select}',
        description: 'Tooltip text for entity icon in main toolbar',
      },
      {
        br: <br />,
        entity: entity.handle,
        select:
          selected || (!isSelectedTokenized && !isTokenizedEntity)
            ? ''
            : ` (${intl.formatMessage({ defaultMessage: 'sign in via a new window' })})`,
      },
    );
  }, [entity, intl, isTokenizedEntity, isSelectedTokenized, selected]);

  return (
    <Tooltip text={tooltipText} location="east">
      <button
        className={classNames(
          styles.workspace,
          !overlayVisible && 'no-drag',
          selected && styles.selected,
        )}
        onClick={async () => {
          setIsMenuOpen(false);

          if (selected) {
            return;
          }

          if (isTokenizedEntity) {
            return openOAuth2URL();
          }

          if (isSelectedTokenized) {
            return window.open(lqnAuthUrl, '_blank')?.focus();
          }

          try {
            const response = await liquidNobleApi.post(`/entity/switch/`, {
              entity_id: profile.entityId,
            });
            const { data } = response;
            if (data?.lockbox_id) {
              setActiveEntityId(data.entity_id);
              saveEntityId(data?.entity_id);
              saveProfileId(data?.profile_id);
              setActiveLockboxId(data?.lockbox_id);
              console.log('Switched entity to: ', data.entity_id);
              refetchCombinedBalance(true, data.entity_id);
              redirect('/dashboard');
            }
          } catch (error) {
            loadProfileList(true);
            errorResponseNotify(error);
          }
        }}
      >
        {!isTokenizedEntity && (
          <div>
            <EntityIcon
              src={entity?.avatarUrl}
              className={classNames(styles.avatar, {
                [styles.selectedAvatar]: selected,
              })}
              size="large"
              displayName={entity?.name}
              inverted
            />
            {/* <LiquidNobleLogo
              width={40}
              height={40}
              className={classNames(styles.providerLogo)}
            /> */}
          </div>
        )}
        {isTokenizedEntity && (
          <TokenizedLogo
            className={classNames(styles.providerLogo, {
              [styles.selectedAvatar]: isSelectedTokenized,
            })}
          />
        )}
        <ActivityIndicator
          profileId={profile?.id}
          className={styles.activityIndicator}
          useActivityHook={useAnyActivityCount}
        >
          <div className={styles.activity}></div>
        </ActivityIndicator>
      </button>
    </Tooltip>
  );
}

export default function MainToolbar({ isMenuOpen, setIsMenuOpen }) {
  const history = useHistory();
  const location = useLocation();
  const redirect = useRedirect();
  const isLoggedIn = useAnyProviderLoggedIn();

  const refetchLockboxBalance = useLockboxBalanceRefetch();
  const selectedProfileId = useSelectedProfileId();
  const [profiles, loadProfileList] = useLiquidNobleActiveProfiles();
  const openSettings = () =>
    history.push(locationToOpenSettings(location, 'security', null));

  const needsMigration =
    !!profiles?.find((profile) => profile.needsMigration) ?? false;

  React.useEffect(() => {
    if (profiles?.length && needsMigration) {
      redirect('/add-account');
    }
  }, [needsMigration, redirect, profiles]);

  // Sort by entity name
  const sortedProfiles = React.useMemo(() => {
    if (!profiles) {
      return [];
    }

    return profiles.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    });
  }, [profiles]);

  const selectedProfile = profiles?.find(
    (profile) => profile.id === selectedProfileId,
  );
  const [supportOpen, setSupportOpen] = React.useState(false);
  const supportButtonRef = React.useRef(null);
  return (
    <nav
      className={classNames(styles.toolbar, {
        [styles.isMenuOpen]: isMenuOpen,
      })}
    >
      <div className={styles.workspaces}>
        {isLoggedIn && (
          <>
            {sortedProfiles
              .filter((profile) => !profile.isTemporary)
              .map((profile) => (
                <ProfileIcon
                  key={profile.id}
                  profile={profile}
                  selectedProfile={selectedProfile}
                  setIsMenuOpen={setIsMenuOpen}
                  loadProfileList={loadProfileList}
                  refetchCombinedBalance={() =>
                    refetchLockboxBalance('combined')
                  }
                />
              ))}
          </>
        )}
      </div>
      <div className={styles.tools}>
        <div />
        <div className={styles.toolsBottom}>
          <div className={styles.supportButtonWrap}>
            <Tooltip
              text={
                <FormattedMessage
                  defaultMessage="Help & Support"
                  description="Main toolbar support button tooltip"
                />
              }
            >
              <Button
                icon
                inverted
                className={styles.supportButton}
                innerRef={supportButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setSupportOpen((open) => !open);
                }}
              >
                <HelpCircleIcon size="tiny" />
              </Button>
            </Tooltip>
            {supportOpen && (
              <SupportPopover
                supportButtonRef={supportButtonRef}
                onClose={() => {
                  setSupportOpen(false);
                }}
              />
            )}
          </div>
          {isLoggedIn && !selectedProfile?.isTemporary && (
            <Tooltip
              text={
                <FormattedMessage
                  defaultMessage="Your account"
                  description="Main toolbar button tooltip"
                />
              }
            >
              <Button
                icon
                inverted
                className={styles.userSettingsDropdown}
                onClick={openSettings}
              >
                <UserCircleIcon size="tiny" />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </nav>
  );
}
